import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {SubSink} from '@Common/core/utils/subsink';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Observable, Subject} from 'rxjs';
import {ApplicationEnum} from '@Common/widgets/general/attachments/interfaces/application.enum';
import {ObjectGeneric} from '@Common/widgets/general/object-generic-django.interface';
import {AttachmentModel} from '@Common/widgets/general/attachments/interfaces/attachment.model';
import * as FileSaver from 'file-saver';
import {AttachmentService} from '@Common/widgets/general/attachments';
import {finalize, switchMap, tap} from 'rxjs/operators';
import {DialogData, DialogValidationComponent} from '@Common/widgets/general/dialog-validation';
import {MatDialog} from '@angular/material/dialog';
import {FormModel, ResponseStateInstruction} from 'app/demat/common/components/form/models';
import {ResponseAvisService} from 'app/demat/common/services/avis/response-avis.service';
import {AvisEnum, ResponseAvis, TypeAvisEnum} from 'app/demat/common/services/avis/response-avis.model';
import {SnackBarToastyService, typeToast} from '@Common/widgets/general/snack-bar';
import {ResponsesService} from '../../../common/services/responses/responses.service';
import {ResponseForm} from "../../../common/components/form/models/responsedemat.model";
import {PubSubService} from "@Common/core/services";

@Component({
  selector: 'app-opinion',
  templateUrl: './opinion.component.html',
  styleUrls: ['./opinion.component.scss']
})
export class OpinionComponent implements OnInit, OnDestroy {
  @Input({ required: true }) responseUUID: string;

  subSink = new SubSink();
  opinion: ResponseAvis;
  AvisEnum = AvisEnum;

  opinionForm: UntypedFormGroup;

  attachmentGeneric: ObjectGeneric = <ObjectGeneric>{};
  readonly maxFilesize = 5 * 1024 * 1024;
  readonly extensionsFiles = '.pdf,.png';
  readonly application = ApplicationEnum.demat;
  readonly uploaderConnector: Subject<ObjectGeneric> = new Subject();
  hasAttachments = false;
  uploading = false;
  hasDownload = false;
  downloading = false;
  readOnlyOpinion = false;
  visibilityAvis = false;
  lastInstructionState: ResponseStateInstruction;
  response: ResponseForm;
  constructor(
    public dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    private opinionSvc: ResponseAvisService,
    private attachmentSvc: AttachmentService,
    private snackSvc: SnackBarToastyService,
    public responseSvc: ResponsesService,
    private pubSubSvc: PubSubService
  ) {
    this._initform();
    this.subSink.sink = this.opinionForm.get('avis')?.valueChanges.subscribe(value => this.updateMotivationValidation(value));
  }

  ngOnInit(): void {
    this.pubSubSvc.publish('loading', true);
    this._getOpinion();
  }
  private _getOpinion() {
    this.subSink.sink = this.opinionSvc
      .myOpinionExterne(this.responseUUID)
      .pipe(
        tap(opinion => {
          this.opinion = opinion;
          this.hasDownload = !!opinion?.attachment_obj?.length;
          this.readOnlyOpinion =
            opinion?.response_obj?.last_instruction_obj?.state !== ResponseStateInstruction.in_progress;
          this.visibilityAvis = opinion?.response_obj?.form_obj?.visibility_avis || false;
          this.lastInstructionState = this.response?.last_instruction_obj?.state;
          this._setForm();

          console.log('OpinionComponent: ', opinion);
          console.log('OpinionComponent hasDownload: ', this.hasDownload);
          console.log('OpinionComponent readOnlyOpinion: ', this.readOnlyOpinion);
        }),
        switchMap(opinion =>
          this.responseSvc.getResponseForOpinion(opinion!.response_obj!.uuid) as Observable<ResponseForm>
        ),
        finalize(() => this.pubSubSvc.publish('loading', false)),
        tap((response: ResponseForm) => (this.response = response)),
      ).subscribe(() => this.pubSubSvc.publish('loading', false));
  }
  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  displayOpinionType() {
    return TypeAvisEnum.toStringLong(this.opinion?.type_avis);
  }

  private _initform() {
    this.opinionForm = this.formBuilder.group({
      avis: ['', Validators.required],
      motivation: ['', Validators.required],
    });
  }

  updateMotivationValidation(avisValue: number) {
    const motivationControl = this.opinionForm.get('motivation');
    if (avisValue === AvisEnum.unfavorable || avisValue === AvisEnum.suspended_other) {
      motivationControl.setValidators([Validators.required]);
    } else {
      motivationControl.clearValidators();
    }

    motivationControl.updateValueAndValidity();
  }

  private _setForm() {
    this.opinionForm.patchValue({
      avis: this.opinion?.avis,
      motivation: this.opinion?.motivation,
    });
  }

  saveOpinion() {
    this.pubSubSvc.publish('loading', true);
    this.opinion.avis = this.opinionForm.value.avis;
    this.opinion.motivation = this.opinionForm.value.motivation;
    this.subSink.sink = this.opinionSvc
      .update(this.opinion)
      .pipe(finalize(() => this.pubSubSvc.publish('loading', false)))
      .subscribe(
      (res) => this.snackSvc.toasty('Donner votre avis', 'Votre avis a été envoyé', typeToast.success)
    );
  }

  handleFilesUploaded(attachments: AttachmentModel[]): void {
    this.opinion.attachment_obj = attachments;
    this.hasAttachments = false;
    this.uploading = false;
    this.hasDownload = true;
  }

  setMetaFile(attachments: AttachmentModel[]) {
    this.hasAttachments = attachments.length > 0;
  }

  uploadFile() {
    this.attachmentGeneric.application = ApplicationEnum.demat;
    this.attachmentGeneric.object_id = this.opinion.id;
    this.attachmentGeneric.content_type = this.opinion.content_type_opinion;

    this.uploading = true;
    this.uploaderConnector.next(this.attachmentGeneric);
  }

  onDownload(attachement: AttachmentModel) {
    this.downloading = true;
    this.subSink.sink = this.attachmentSvc.download(attachement)
      .pipe(
        finalize(() => this.downloading = false)
      )
      .subscribe(v => FileSaver.saveAs(v, attachement.filename));
  }

  onDelete(attachement: AttachmentModel) {
    const data = new DialogData();
    data.title = 'Confirmation de suppression de fichier';
    data.message = `Souhaitez vous supprimer la pièce jointe (${attachement.filename}) ? `;

    const dialogRef = this.dialog.open(DialogValidationComponent, {data: data});
    dialogRef.updatePosition({top: '40px'});
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.subSink.sink = this.attachmentSvc.delete(attachement)
          .subscribe(() => {
            this.hasDownload = false;
            this.opinion.attachment_obj = [];
          });
      }
    });
  }

  filename(attachement: AttachmentModel) {
    if (attachement.filename) { return attachement.filename; }
    return attachement.name;
  }
}
